/**
 * useAdminSocket Hook
 *
 * Exposes admin socket state and methods to AdminToolsPage.
 * Connect on mount (with token) and disconnect on unmount; use onRoomsSnapshot
 * to receive rooms snapshot and requestRoomsSnapshot() to request a fresh one.
 *
 * Used by: AdminToolsPage
 */

import { useCallback, useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';
import { adminSocketService } from './adminSocket.service';
import type { AdminSnapshot } from './types';

export interface UseAdminSocketReturn {
    socket: Socket | null;
    isConnected: boolean;
    connectError: string | null;
    connect: (token: string) => void;
    disconnect: () => void;
    onRoomsSnapshot: (handler: (payload: AdminSnapshot) => void) => () => void;
    requestRoomsSnapshot: () => void;
}

/**
 * useAdminSocket hook
 *
 * Returns admin socket, connection state, connect/disconnect, onRoomsSnapshot,
 * and requestRoomsSnapshot. Call connect(token) on mount and disconnect() on unmount.
 * Updating state after connect() forces a re-render so we subscribe to the new socket.
 */
export function useAdminSocket(): UseAdminSocketReturn {
    const [connectTrigger, setConnectTrigger] = useState(0);
    const socket = adminSocketService.getSocket();
    const [isConnected, setConnected] = useState(() => adminSocketService.isConnected());
    const [connectError, setConnectError] = useState<string | null>(null);

    const connect = useCallback((token: string) => {
        adminSocketService.connect(token);
        setConnectError(null);
        setConnectTrigger((n) => n + 1);
    }, []);

    // Reflect socket connect/disconnect/error in state
    useEffect(() => {
        if (!socket) return;

        const onConnect = () => {
            setConnected(true);
            setConnectError(null);
        };
        const onDisconnect = () => setConnected(false);
        const onConnectError = (err: Error) => {
            setConnected(false);
            setConnectError(err.message || 'Connection failed');
        };

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('connect_error', onConnectError);

        if (socket.connected) {
            setConnected(true);
            setConnectError(null);
        }

        return () => {
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('connect_error', onConnectError);
        };
    }, [socket, connectTrigger]);

    return {
        socket,
        isConnected,
        connectError,
        connect,
        disconnect: () => adminSocketService.disconnect(),
        onRoomsSnapshot: (handler: (payload: AdminSnapshot) => void) =>
            adminSocketService.onRoomsSnapshot(handler),
        requestRoomsSnapshot: () => adminSocketService.requestRoomsSnapshot(),
    };
}
